import React, {useState} from "react";
import {useTranslation} from "react-i18next";

import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import {FileUpload, Send as SendIcon} from "@vedmarket/ui-kit";

import {useCreateSupportTicketMessageMutation} from "../../../../../../../api/support";
import AlertBottom from "../../../../../../common/alertBottom";
import {
  SUPPORT_ATTACHMENT_ACCEPTED_TYPES,
  SUPPORT_ATTACHMENT_MAX_SIZE,
} from "../../../../support-constants";

import './support-request-reply-form.scss'

type Props = {
  onCancel(): void
  onSent(): Promise<void>
  ticketId: string
}

type AlertState = {
  isError: boolean
  message: string
  open: boolean
}

const SupportRequestReplyForm = ({onCancel, onSent, ticketId}: Props) => {

  const { t } = useTranslation();

  const [ createSupportTicketMessage, {isLoading} ] = useCreateSupportTicketMessageMutation();

  const [ text, setText ] = useState('');
  const [ attachments, setAttachments ] = useState<File[]>([]);

  const [ alert, setAlert ] = useState<AlertState>({
    isError: false,
    message: '',
    open: false,
  });

  const handleFile = (file: File) => {
    if (file.size > SUPPORT_ATTACHMENT_MAX_SIZE) {
      setAlert({
        isError: true,
        message: t('support__message__attachmentTooLarge'),
        open: true,
      });

      return false;
    }

    setAttachments((files) => [ ...files, file ]);

    return true;
  }

  const handleRemoveFile = (index: number) => {
    setAttachments((files) => files.filter((file, fileIndex) => fileIndex !== index));
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const messageText = text.trim();

    if (!messageText || isLoading) {
      return;
    }

    try {
      await createSupportTicketMessage({
        attachments,
        text: messageText,
        ticketId,
      }).unwrap();

      setText('');
      setAttachments([]);
      await onSent();
      setAlert({
        isError: false,
        message: t('support__message__messageSent'),
        open: true,
      });
    } catch {
      setAlert({
        isError: true,
        message: t('support__message__messageSendFailed'),
        open: true,
      });
    }
  }

  const handleCloseAlert = () => {
    setAlert((value) => ({...value, open: false}));
  }

  return (
    <form className='support-request-reply-form' onSubmit={handleSubmit}>
      <Card className='support-request-reply-form__card'>
        <TextField
          fullWidth
          multiline
          label={t('support__text__requestMessage')}
          value={text}
          onChange={(event) => setText(event.target.value)}
          InputLabelProps={{shrink: true}}
          minRows={5}
          sx={{'.MuiInputBase-root': {alignItems: 'flex-start', height: '160px'}}}
        />
        {attachments.map((file, index) => (
          <FileUpload
            key={`${file.name}-${file.lastModified}-${index}`}
            acceptedFilesConfig={SUPPORT_ATTACHMENT_ACCEPTED_TYPES}
            initialFileTitle={file.name}
            onRemove={() => handleRemoveFile(index)}
          />
        ))}
        <FileUpload
          key={`support-message-attachment-${attachments.length}`}
          acceptedFilesConfig={SUPPORT_ATTACHMENT_ACCEPTED_TYPES}
          onHandleFile={handleFile}
        />
      </Card>
      <div className='support-request-reply-form__actions'>
        <Button
          type='button'
          variant='outlined'
          onClick={onCancel}
          sx={{cursor: 'pointer', height: '40px', padding: '8px 24px'}}
        >
          {t('actions__text__cancel')}
        </Button>
        <Button
          type='submit'
          disabled={!text.trim() || isLoading}
          variant='contained'
          startIcon={<SendIcon />}
          sx={{cursor: 'pointer', height: '40px', padding: '8px 24px'}}
        >
          {t('support__action__sendMessage')}
        </Button>
      </div>
      <AlertBottom
        open={alert.open}
        isError={alert.isError}
        message={alert.message}
        onClose={handleCloseAlert}
      />
    </form>
  );
};

export default SupportRequestReplyForm;
